import { EStudentConnectionStatus, EStudentPaymentStatus } from '@constants';
import { ICourseStudentStats, ITag } from '@sdk/contracts';
import { Table, Tooltip } from 'antd';
import { TFunction } from 'i18next';
import { AlignType, FixedType } from 'rc-table/lib/interface';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Wrapper } from './Students.styles';
import { StudentsHeader } from './StudentsHeader';

import { EditStudentDrawer } from '@/components/EditStudentDrawer';
import { Icon } from '@/components/Icon';
import { IsUserOnline } from '@/components/IsUserOnline';
import { StudentConnectionStatusTag } from '@/components/StudentConnectionStatusTag';
import { StudentPreview } from '@/components/StudentPreview';
import { SelectionWrapper, TooltipWrapper } from '@/components/StudentsList';
import { ChangeStatusInBulkModal } from '@/components/StudentsList/ChangeStatusInBulkModal';
import { PaymentStatus } from '@/components/StudentsList/PaymentStatus';
import { Tags } from '@/components/Tags';
import { useStudentPreview } from '@/hooks';
import { useCourseStudentsStats, useProfile } from '@/store';

export const Students = () => {
  const { t } = useTranslation('DefaultTranslations');
  const { students, isLoading, setPageAndLimit, pagination } = useCourseStudentsStats();
  const { setUserId } = useStudentPreview();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const {
    roleCheck: { isManager }
  } = useProfile();

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handlePreviewUser = (userId: string) => setUserId(userId);

  const handleChangeStatus = () => {
    if (!selectedRowKeys.length) return;
    setIsChangeStatusModalOpen(true);
  };

  return (
    <Wrapper>
      <StudentsHeader />
      <Table
        rowKey="_id"
        tableLayout="fixed"
        loading={isLoading}
        columns={columns(t, isManager)}
        dataSource={students}
        scroll={{ y: window.innerHeight - 250, x: 1200 }}
        onRow={userStats => ({
          onClick: () => handlePreviewUser(userStats.user._id)
        })}
        rowSelection={
          isManager
            ? {
                selections: [
                  Table.SELECTION_ALL,
                  Table.SELECTION_NONE,
                  {
                    key: 'changeStatus',
                    text: <SelectionWrapper>{t('CHANGE_STATUS')}</SelectionWrapper>,
                    onSelect: handleChangeStatus
                  }
                ],
                selectedRowKeys,
                onChange: onSelectChange
              }
            : undefined
        }
        pagination={{
          pageSize: pagination?.limit,
          total: pagination?.totalDocs,
          showTotal: total => t('STUDENTS_TOTAL_COUNT', { total }),
          onChange: (page, limit) => setPageAndLimit(page, limit)
        }}
      />
      <ChangeStatusInBulkModal
        isOpen={isChangeStatusModalOpen}
        setIsOpen={setIsChangeStatusModalOpen}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <StudentPreview />
      <EditStudentDrawer />
    </Wrapper>
  );
};

const columns = (t: TFunction<'DefaultTranslations'>, isManager: boolean) => [
  {
    title: '',
    dataIndex: '_id',
    width: 20,
    align: 'center' as const,
    key: '_id',
    render: (userId: string) => <IsUserOnline userId={userId} />
  },
  {
    title: t('NAME'),
    width: 120,
    dataIndex: ['user', 'name'],
    key: 'name',
    fixed: 'left' as FixedType,
    ellipsis: true
  },
  {
    title: t('LASTNAME'),
    dataIndex: ['user', 'lastName'],
    width: 160,
    key: 'lastName',
    fixed: 'left' as FixedType,
    ellipsis: true
  },
  {
    title: t('EMAIL'),
    dataIndex: ['user', 'email'],
    width: 200,
    key: 'email',
    ellipsis: true
  },
  {
    key: 'tags',
    title: t('TAGS'),
    dataIndex: ['user', 'tags'],
    width: 220,
    render: (tags: ITag[]) => <Tags tags={tags} />
  },
  {
    key: 'quizNumber',
    title: t('QUIZ_NUMBER'),
    dataIndex: 'quizNumber',
    width: 120,
    align: 'center' as AlignType
  },
  {
    key: 'avgScore',
    title: t('AVG_SCORE'),
    dataIndex: 'avgScore',
    width: 80,
    align: 'center' as AlignType,
    render: (avgScore: number) => avgScore && <span>{avgScore.toFixed(2)}</span>
  },
  {
    key: 'ticketNumber',
    title: t('TICKETS_NUMBER'),
    dataIndex: 'ticketNumber',
    width: 100,
    align: 'center' as AlignType
  },
  ...(isManager
    ? [
        {
          key: 'csat',
          title: t('CSAT_TITLE'),
          dataIndex: 'csat',
          width: 100,
          align: 'center' as AlignType
        },
        {
          key: 'appTime',
          title: (
            <Tooltip title={t('APP_TIME_TOOLTIP')}>
              <TooltipWrapper>
                <span>{t('APP_TIME')}</span>
                <Icon i="Information" />
              </TooltipWrapper>
            </Tooltip>
          ),
          dataIndex: 'appTime',
          width: 150,
          align: 'center' as AlignType
        },
        {
          key: 'connectionStatus',
          title: t('ACTIVITY_STATUS'),
          dataIndex: 'connectionStatus',
          width: 100,
          align: 'center' as AlignType,
          render: (connectionStatus: EStudentConnectionStatus) => (
            <StudentConnectionStatusTag iconOnly userConnectionStatus={connectionStatus} />
          )
        },
        {
          key: 'paymentStatus',
          title: t('PAYMENT_STATUS'),
          dataIndex: 'paymentStatus',
          width: 100,
          align: 'center' as AlignType,
          render: (paymentStatus: EStudentPaymentStatus, userStats: ICourseStudentStats) => (
            <PaymentStatus userId={userStats.user._id} status={paymentStatus} />
          )
        }
      ]
    : [])
];
